import { useEffect, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import { LangProvider } from "@/lib/siteI18n";
import { trackPageView } from "@/lib/tracking";
import { applyCanonical } from "@/lib/seo";
import DiagnosticInaugural from "@/pages/DiagnosticInaugural";
import Desinscription from "@/pages/Desinscription";
import { ErrorBoundary } from "@/components/ErrorBoundary";

import Home from "@/pages/Home";
import Services from "@/pages/Services";
import About from "@/pages/About";
import Journal from "@/pages/Journal";
import ArticlePage from "@/pages/ArticlePage";
import AdminRadar from "@/pages/AdminRadar";
import Checkout from "@/pages/Checkout";
import Merci from "@/pages/Merci";
import MentionsLegales from "@/pages/MentionsLegales";
import CGV from "@/pages/CGV";
import Confidentialite from "@/pages/Confidentialite";
import Portal from "@/pages/Portal";
import PortalVerify from "@/pages/PortalVerify";
import PortalDashboard from "@/pages/PortalDashboard";
import Contact from "@/pages/Contact";
import AdminV2 from "@/pages/AdminV2";
import Bilan from "@/pages/Bilan";
import PreDiagnostic from "@/pages/PreDiagnostic";
import DiagnosticSouverainete from "@/pages/DiagnosticSouverainete";
import CoachingElite from "@/pages/CoachingElite";
import CoachingSouverain from "@/pages/CoachingSouverain";
import KitDigital from "@/pages/KitDigital";
import EmailConfirmed from "@/pages/EmailConfirmed";
import EmailError from "@/pages/EmailError";
import AnalysePrecision from "@/pages/AnalysePrecision";
import RapportQuestionnaire from "@/pages/RapportQuestionnaire";

const ScrollAndTrack = () => {
  const location = useLocation();
  const lastPath = useRef(null);
  useEffect(() => {
    window.scrollTo(0, 0);
    applyCanonical(location.pathname);
    if (lastPath.current !== location.pathname) {
      lastPath.current = location.pathname;
      trackPageView(location.pathname);
    }
  }, [location.pathname]);
  return null;
};

function SmoothScroll() {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.1, smoothWheel: true });
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
    };
  }, []);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <SmoothScroll />
        <ScrollAndTrack />
        <LangProvider>
        <ErrorBoundary>
        <Routes>
          {/* Landing standalone Meta Ads — hors Layout (pas de Header/Footer/chatbot AETHERIS) */}
          <Route path="/bilan" element={<Bilan />} />
          <Route path="/pre-diagnostic" element={<PreDiagnostic />} />
          <Route path="/diagnostic-souverainete" element={<DiagnosticSouverainete />} />
          <Route element={<Layout />}>
            <Route path="/" element={<Home />} />
            <Route path="/services" element={<Services />} />
            <Route path="/a-propos" element={<About />} />
            <Route path="/journal" element={<Journal />} />
            <Route path="/journal/:slug" element={<ArticlePage />} />
            {/* Alias public "/blog" — même contenu que /journal */}
            <Route path="/blog" element={<Journal />} />
            <Route path="/blog/:slug" element={<ArticlePage />} />
            {/* /diagnostic devient une redirection vers /bilan (funnel unifié) */}
            <Route path="/diagnostic" element={<Navigate to="/bilan" replace />} />
            <Route path="/coaching-elite" element={<CoachingElite />} />
            <Route path="/coaching-souverain" element={<CoachingSouverain />} />
            <Route path="/kit-digital-premium" element={<KitDigital />} />
            <Route path="/analyse-de-precision" element={<AnalysePrecision />} />
            <Route path="/rapport-triple-fusion" element={<Navigate to="/analyse-de-precision" replace />} />
            <Route path="/diagnostic-inaugural" element={<DiagnosticInaugural />} />
            <Route path="/desinscription" element={<Desinscription />} />
            <Route path="/analyse-de-precision/questionnaire" element={<RapportQuestionnaire />} />
            <Route path="/rapport-triple-fusion/questionnaire" element={<RapportQuestionnaire />} />
            <Route path="/admin/radar" element={<AdminRadar />} />
            <Route path="/checkout/:productId" element={<Checkout />} />
            <Route path="/merci" element={<Merci />} />
            <Route path="/email-confirmed" element={<EmailConfirmed />} />
            <Route path="/email-error" element={<EmailError />} />
            <Route path="/mentions-legales" element={<MentionsLegales />} />
            <Route path="/cgv" element={<CGV />} />
            <Route path="/confidentialite" element={<Confidentialite />} />
            <Route path="/portal-access" element={<Portal />} />
            {/* Alias : /mon-espace pointe vers /portal-access */}
            <Route path="/mon-espace" element={<Portal />} />
            {/* Magic Link Portal — accès clients payants */}
            <Route path="/espace" element={<Portal />} />
            <Route path="/espace/verify" element={<PortalVerify />} />
            <Route path="/espace/dashboard" element={<PortalDashboard />} />
            <Route path="/contact" element={<Contact />} />
          </Route>
          {/* Back Office — hors Layout public (pas de Header/Footer/chatbot) */}
          <Route path="/admin" element={<AdminV2 />} />
          <Route path="/admin/dashboard" element={<AdminV2 />} />
        </Routes>
        </ErrorBoundary>
        </LangProvider>
        <Toaster position="top-center" />
      </BrowserRouter>
    </div>
  );
}

export default App;
